import type { Address, Team, TeamClaim } from '@baseball-near-you/prisma';
import { Button, Separator } from '@baseball-near-you/ui';
import { AddReviewModal } from '@bb/components/Reviews/AddReviewModal';
import { useUserProfile } from '@bb/lib/hooks/useUserProfile';
import { useUserTeamInterestMutation } from '@bb/lib/mutations/useUserTeamInterestMutation';
import { BellPlus, BellRing, CheckCircle2, CircleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { TeamSelectionSearch } from '../../Settings/Team/ClaimTeam/TeamSelection/TeamSelectionSearch';

export function ReviewStep(props: {
  onStepSuccess: () => Promise<void>;
}) {
  const [search, setSearch] = useState('');
  const [selectedTeam, setSelectedTeam] = useState<
    (Team & { address: Address[]; claims: TeamClaim[] }) | null
  >(null);
  const { userProfile } = useUserProfile();
  const isSubscribedToSelectedTeam = useMemo(
    () =>
      !!userProfile?.userTeamInterest?.some((interest) =>
        selectedTeam?.id ? interest.teamId === selectedTeam.id : false
      ),
    [userProfile?.userTeamInterest, selectedTeam?.id]
  );

  const isSelectedTeamClaimed = useMemo(
    () => selectedTeam && selectedTeam.claims.length > 0,
    [selectedTeam]
  );

  const { createUserTeamInterest, creatingUserTeamInterest } =
    useUserTeamInterestMutation();

  return (
    <div className="flex flex-col">
      <TeamSelectionSearch
        search={search}
        setSearch={setSearch}
        setSelectedTeam={setSelectedTeam as any}
        selectedTeam={selectedTeam}
        onTeamSelect={() => {}}
        highlightedTeams={
          userProfile?.userTeamInterest?.map(
            (interest) => interest.team.slug
          ) ?? []
        }
      />

      <Separator className="my-2 w-full shadow-lg" />
      {!!selectedTeam && (
        <div className="flex flex-col justify-between overflow-hidden rounded-lg border">
          {!isSelectedTeamClaimed && (
            <div className="flex items-center gap-1 bg-gray-300 px-2 pt-1 pb-2">
              {isSubscribedToSelectedTeam ? (
                <>
                  <CheckCircle2 size={25} className="mx-2 text-green-600" />
                  <p className="py-1.5 text-green-700 text-sm">
                    You will be notified when this team is claimed!
                  </p>
                </>
              ) : (
                <>
                  <CircleAlert size={40} className="mx-2 text-gray-600" />
                  <p className="py-1.5 text-gray-700 text-sm">
                    This team hasn't been claimed yet - subscribe to get
                    notified by email when they join the platform!
                  </p>
                </>
              )}
            </div>
          )}
          <div className="flex justify-between bg-gray-100 p-4">
            <div className="flex flex-col justify-around">
              <p className="truncate font-medium text-base">
                {selectedTeam.name}
              </p>
              {(selectedTeam.address?.[0]?.city ||
                selectedTeam.address?.[0]?.state) && (
                <p className="text-gray-500 text-sm">
                  {selectedTeam.address[0].city}
                  {selectedTeam.address[0].state &&
                    `${selectedTeam.address[0].city ? ', ' : ''}${selectedTeam.address[0].state}`}
                </p>
              )}
            </div>
            {isSelectedTeamClaimed ? (
              <AddReviewModal
                teamSlug={selectedTeam.slug}
                onSubmit={async (team) => {
                  await props.onStepSuccess();
                }}
              />
            ) : isSubscribedToSelectedTeam ? (
              <Button disabled className="bg-green-700 text-white opacity-100!">
                <BellRing size={18} className="mr-2" />
                Subscribed!
              </Button>
            ) : (
              <Button
                onClick={async () => {
                  await createUserTeamInterest({
                    teamId: selectedTeam.id,
                    userId: userProfile!.id,
                  });
                }}
                disabled={creatingUserTeamInterest}
                loading={creatingUserTeamInterest}
              >
                <BellPlus size={18} className="mr-2" />
                Subscribe
              </Button>
            )}
          </div>
        </div>
      )}
      <Button
        className="mt-4 self-end bg-red"
        onClick={() => props.onStepSuccess()}
      >
        Continue
      </Button>
    </div>
  );
}
